// File for logic corresponding to article-list-tabs component
import { Component } from 'js-component-framework';
import { AriaTablist } from 'js-component-framework/lib/plugins';
import Debounce from 'utils/Debounce';
import './article-list-tabs.scss';

/**
 * Component for article-list-tabs
 */
export default class ArticleListTabs extends Component {
  /**
   * Start the component
   */
  constructor(config) {
    super(config);

    this.tablist = new AriaTablist({
      tablist: this.children.tablist,
      panels: this.children.panels,
    });
    this.tablist.init();

    this.shouldUpdateLayout = this.shouldUpdateLayout.bind(this);
    this.buildMoreTab = this.buildMoreTab.bind(this);

    this.onResizeDebounce = new Debounce(
      this.shouldUpdateLayout,
      'resize',
      window,
      250,
      false,
    );

    this.shouldUpdateLayout();
    this.buildMoreTab();

    this.element.classList.add(this.options.isLoadedClass);
  }

  /**
   * Build the more tab element and return it.
   *
   * @returns {element} moreTab
   */
  getMoreTab() {
    const moreTab = document.createElement('li');
    moreTab.setAttribute('role', 'presentation');
    moreTab.classList.add(this.options.moreTabClass);
    return moreTab;
  }

  /**
   * Build the more button element and return it.
   *
   * @returns {element} moreButton
   */
  getMoreButton() {
    const moreButton = document.createElement('button');
    const { more } = window.nexstar.template_variables.l10n;
    moreButton.setAttribute('aria-haspopup', true);
    moreButton.setAttribute('aria-expanded', false);
    moreButton.innerText = more;

    // Add event listener to more button.
    moreButton.addEventListener('click', () => {
      const expanded = moreButton.getAttribute('aria-expanded');
      if ('true' === expanded) {
        moreButton.setAttribute('aria-expanded', false);
      } else {
        moreButton.setAttribute('aria-expanded', true);
      }
    });

    // Handle an outside click to more button.
    document.addEventListener('click', (event) => {
      const expanded = moreButton.getAttribute('aria-expanded');
      const moreTab = document.querySelector(`.${this.options.moreTabClass}`);
      if (
        'true' === expanded
        && ! moreTab.contains(event.target)) {
        moreButton.setAttribute('aria-expanded', false);
      }
    });
    return moreButton;
  }

  /**
   * Build the more tab and dropdown.
   */
  buildMoreTab() {
    const { tabs } = this.children;

    const { moreTabThreshold, moreTabOffset } = this.element.dataset;

    const overflowTabs = this.element.querySelectorAll(`.article-list-tabs__tablist > li:nth-child(n+${moreTabThreshold - (moreTabOffset - 1)})`); // eslint-disable-line max-len

    if (
      0 === tabs.length
      || moreTabThreshold > tabs.length
      || (5 < moreTabThreshold && moreTabThreshold >= tabs.length)) {
      return;
    }

    const moreTab = this.getMoreTab();
    const moreButton = this.getMoreButton();
    const morelist = document.createElement('ul');

    // Determine where the More tab is placed, needs to reference the array index, not the number in the config.
    tabs[moreTabThreshold - moreTabOffset]
      .insertAdjacentElement('beforebegin', moreTab);

    moreTab.appendChild(moreButton);
    moreTab.appendChild(morelist);

    overflowTabs.forEach((overflowTab) => {
      morelist.appendChild(overflowTab);
    });
  }

  /**
   * Determine if we are displaying tabbed or non-tabbed layout.
   */
  shouldUpdateLayout() {
    const { stackLayoutUntilBreakpoint: stackBp } = this.element.dataset;
    const { tablist } = this.children;
    const { isTearDownClass } = this.options;

    // Tablist can also appear in a "stacked" layout based on breakpoint.
    if (matchMedia(`(min-width: ${stackBp}px)`).matches) {
      if ('tablist' !== tablist.getAttribute('role')) {
        this.tablist.init();
        this.element.classList.remove(isTearDownClass);
      }
    } else if (matchMedia(`(max-width: ${stackBp}px)`).matches) {
      this.tablist.tearDown();
      this.element.classList.add(isTearDownClass);
    }
  }
}
